import React, { useEffect, useState, useContext } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom'; // <--- Importações atualizadas
import { SessionContext } from '@/providers/SessionProvider';
import { useMutation } from 'jsonapi-react';
import { useClient } from 'jsonapi-react';
import usePermission from '@/hooks/usePermission';
import Error404 from '@/app/components/Error404';

// O componente PrivateRoute agora é renderizado DENTRO de um <Route element={<PrivateRoute as={Componente} ... />} />
// Isso significa que ele não recebe mais "path" ou "uri" diretamente como props do <Route>.
// Em vez disso, ele recebe "as", "roleName", "roleGroup", "enabled" e outras props que você passa.
function PrivateRoute({ as: Comp, roleName, roleGroup, enabled, ...restProps }) {
  const [loading, setLoading] = useState(true);
  const [logged, setLogged] = useState(true);
  const [addRouteLog] = useMutation('route_logs');
  const state = useContext(SessionContext);
  const client = useClient();
  const location = useLocation(); // Hook para acessar o objeto de localização atual
  const params = useParams(); // Hook para acessar os parâmetros da URL
  const hasPermission = usePermission(roleName, roleGroup);

  // Ajuste para obter o caminho atual de forma mais confiável no RRDv6
  // location.pathname é o caminho atual da URL
  const currentPath = location.pathname;

  // Em RRDv6, os parâmetros de rota são acessados via useParams().
  // Para compatibilidade com sua lógica `notificableType`, você precisará
  // mapear os `idKey` para os `params`.
  const pathParams = params;

  useEffect(() => {
    // setCurrentPath(parentProps.path); // Este já foi removido das chamadas do <Route>
    createRequestLog();
    updateNotifications();
  }, [currentPath, pathParams]); // Adicionado dependências para re-executar se a rota ou seus params mudarem

  const updateNotifications = async () => {
    const pathTargetObject = [
      { entity: ['LearningSystem'], paths: ['/show-course/:id/classroom'], idKey: 'id' },
      { entity: ['Slot'], paths: ['/minhas-tarefas/:taskType/:taskId'], idKey: 'taskId' }, // Ajustado para corresponder à sua rota
      { entity: ['Project', 'ProjectVersion'], paths: ['/projetos/:projectId', '/projetos/grupos/:groupId/:projectId/sobre', '/projetos/:projectId/sobre'], idKey: 'projectId' }, // Ajustado
      { entity: ['Post'], paths: ['/posts/:slug'], idKey: 'slug' },
      {
        entity: ['Activity'],
        paths: [
          '/atividade-complementar/:activityId/:courseId',
          '/atividade-complementar/atividade/:id',
          '/atividade-complementar/avaliar-atividades/:id',
          '/atividade-complementar/avaliacao/:activityId/:id',
          '/atividade-complementar/avaliar-atividades/:id',
          '/atividade-complementar/avaliar/:activityId/:id',
          '/atividade-complementar/dados/:id'
        ],
        idKey: 'id'
      }, // Ajustado com mais caminhos
      { entity: ['Content'], paths: ['/materiais/:materialId/:moduleId', '/materiais'], idKey: 'materialId' }, // Ajustado
      { entity: ['Event'], paths: ['/evento/:eventId'], idKey: 'eventId' },
      { entity: ['LearningSystemItem'], paths: ['/lms/visualizar/modulo/:id/disciplina/:disciplineId/aula/:lessonId', '/show-course/:id/classroom'], idKey: 'lessonId' }, // Ajustado para lessonId
      { entity: ['QuestionBook'], paths: ['/questions/:selectedTab/:id'], idKey: 'id' }, // Ajustado
      { entity: ['Survey'], paths: ['/questionario/:representation/:id', '/questionario/:representation/:surveyId/estudante/:studentId', '/questionario/dados/:surveyId/propostas/:questionId'], idKey: 'id' } // Ajustado
    ];

    // Precisa de uma lógica mais robusta para encontrar o match de rota
    const notificableType = pathTargetObject.find(item =>
      item.paths.some(pathPattern => {
        // Regex para comparar o pathPattern com o currentPath
        const regex = new RegExp(`^${pathPattern.replace(/:\w+/g, '([^/]+)')}$`);
        return regex.test(currentPath);
      })
    );

    if (notificableType) {
      // Extrair o ID do parâmetro correto
      let idToUse = null;
      if (notificableType.idKey && pathParams[notificableType.idKey]) {
        idToUse = pathParams[notificableType.idKey];
      } else {
        // Lógica alternativa para caminhos sem idKey explícito ou para IDs genéricos
        // Por exemplo, para '/atividade-complementar', você pode não ter um ID na URL,
        // mas a entidade 'Activity' ainda é relevante para o tipo de notificação.
        // Você pode precisar de uma lógica para lidar com isso se 'idKey' não for sempre presente.
        // Para o caso de '/atividade-complementar', se não houver ID na URL, idToUse seria null
        // e o updateNotifications não seria chamado com um ID inválido.
      }

      if (idToUse || !notificableType.idKey) {
        // Se tem ID ou se não precisa de ID (rota genérica como '/atividade-complementar')
        await client
          .mutate(['update_all/notifications'], {
            'notificable-id': idToUse, // Pode ser null se a rota não tiver ID
            'notificable-type': notificableType.entity
          })
          .catch(error => {
            console.error('Erro ao atualizar notificações:', error);
          });
      }
    }
  };

  const createRequestLog = async () => {
    // Verifica a sessão para evitar loop infinito em caso de erro na API de log
    if (!state.session || Object.keys(state.session).length === 0) {
      setLogged(false);
      localStorage.setItem('session', null);
      localStorage.setItem('redirectUrl', location.pathname); // Use location.pathname
      setLoading(false);
      return;
    }

    const expirationDate = state.session?.user?.tokenExpirationDate || (state.session?.user && state.session?.user['token-expiration-date']);
    const tokenExpirationDate = new Date(expirationDate);

    if (tokenExpirationDate < new Date()) {
      setLogged(false);
      localStorage.setItem('session', null);
      localStorage.setItem('redirectUrl', location.pathname); // Use location.pathname
      setLoading(false);
    } else {
      try {
        await addRouteLog({ path: currentPath, uri: location.pathname }); // Use currentPath e location.pathname
        setLoading(false);
      } catch (responseError) {
        if (responseError?.error?.title === 'Not Authorized') {
          setLogged(false);
          localStorage.setItem('session', null);
          localStorage.setItem('redirectUrl', location.pathname); // Use location.pathname
        }
        setLoading(false);
      }
    }
  };

  // Lógica de renderização
  if (loading) {
    return <></>; // Ou um spinner de carregamento
  }

  if (enabled === false) {
    return <Error404 />; // A feature está desabilitada globalmente
  }

  // Se não estiver logado, redireciona para a página de login
  if (!logged) {
    // Redireciona usando o componente Navigate do react-router-dom
    // state={{ from: location }} é a forma de passar o estado para o login
    // para que ele saiba para onde redirecionar depois
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace // Substitui a entrada atual no histórico
      />
    );
  }

  // Verifica permissões se roleName e roleGroup forem definidos
  // Assumo que getPermission é uma função que verifica as habilidades do usuário
  if (roleName && roleGroup && !hasPermission) {
    return <Error404 />; // Sem permissão para esta rota
  }

  // Se tudo estiver ok, renderiza o componente principal
  return (
    <Comp
      {...restProps}
      uri={currentPath}
      {...params}
    />
  );
}

export default PrivateRoute;
